import {USER_KEYS} from "@/api/userApi";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import RefluencedLogo from "@/assets/images/refluenced.svg";
import Button from "@/components/Common/Button";
import PasswordField from "@/components/Common/forms/PasswordField";
import {useZodState} from "@/hooks/useZodState";
import {client} from "@/httpClient";
import {useMutation} from "@tanstack/react-query";
import {createFileRoute, useRouter} from "@tanstack/react-router";
import {useTranslation} from "react-i18next";
import {useMedia, useTitle} from "react-use";
import {z} from "zod";

const resetPasswordSchema = z
  .object({
    password: z.string().min(8, "passwordAtLeast8Charaters"),
    passwordConfirmation: z.string().min(8, "passwordAtLeast8Charaters"),
  })
  .refine(data => data.password === data.passwordConfirmation, {
    error: "passwordsNotMatching",
    path: ["passwordConfirmation"],
  });

export const Route = createFileRoute("/_unauthenticated/reset-password")({
  component: ResetPassword,
});

function ResetPassword() {
  const {t} = useTranslation("common");
  const navigate = Route.useNavigate();
  const router = useRouter();
  const isTall = useMedia("(min-height: 1200px)");
  const [state, setValue, validate] = useZodState(resetPasswordSchema, {password: "", passwordConfirmation: ""});

  const {mutate: sendRequest, isPending} = useMutation({
    meta: {errorMessage: t("linkExpired"), successMessage: t("passwordChangedSuccessfully")},
    mutationKey: USER_KEYS.mUserPasswordReset(router.state.location.searchStr),
    mutationFn: () =>
      client.post(USER_KEYS.mUserPasswordReset(router.state.location.searchStr)[0], {password: state.password}),
    onSuccess: () => {
      navigate({to: "/login"});
    },
  });

  useTitle(t("Reset Password"));

  return (
    <div className="size-full px-4 sm:px-8 md:px-12">
      <div
        className={
          "flex size-full flex-col items-center justify-center pb-16 sm:px-20 lg:px-8 xl:px-20 " +
          (isTall ? "gap-14" : "gap-10")
        }>
        <div className="flex w-full justify-between">
          <button onClick={() => navigate({to: "/login"})}>
            <RightArrowIcon className="text-primary-sky size-4 shrink-0 rotate-180" />
          </button>
          <span className="font-domaine-display text-primary-midnight text-center text-5xl font-bold">
            {t("Reset Password")}
          </span>
          <div className="w-4.25" />
        </div>
        <div className="mb-5 size-20 shrink-0">
          <img src={RefluencedLogo} className="size-full object-contain" />
        </div>
        <PasswordField
          onChange={newValue => setValue("password", newValue)}
          value={state.password}
          placeholder={t("Password")}
          label={t("Password")}
          error={state.errors.password ? t(state.errors.password as "passwordAtLeast8Charaters") : null}
        />
        <PasswordField
          onChange={newValue => setValue("passwordConfirmation", newValue)}
          value={state.passwordConfirmation}
          placeholder={t("Confirm Password")}
          label={t("Confirm Password")}
          error={
            state.errors.passwordConfirmation
              ? t(state.errors.passwordConfirmation as "passwordAtLeast8Charaters")
              : null
          }
        />
        <Button
          onClick={() => {
            if (validate()) {
              sendRequest();
            }
          }}
          isLoading={isPending}
          className="h-14 w-48"
          buttonStyle={"Gradient"}>
          {t("Change password")}
        </Button>
      </div>
    </div>
  );
}
